import { fetchApi } from "~/lib/api";
import type { AdminGetLyricsFilesParams, AdminGetLyricsReportsParams, AdminGetYoutubeTrendingParams, AdminLyricsFileEntry, AdminTopSearchQuery, AdminYouTubeSearchResult, Artist, BackgroundServiceTriggerResult, ChildrenSongYoutubeTrending, GetLyricsPageStatsParams, GetLyricsTopSearchQueriesParams, LyricsPageStats, LyricsReport, LyricsReportStatus, LyricsSearchParams, LyricsSearchResult, Pagination, SubmitNewLyricsRequest, TrendingLyricsItem } from "~/types";
import { serializeFilterParams } from "./utils";

export type AnonymousLyricsReportRequest = {
  url?: string;
  category: string;
  description?: string;
};

export const anonymousLyricsService = {
  /**
   * Get trending lyrics slugs + view counts
   * Returns up to `limit` items.
   */
  async getTrending(params: { limit?: number } = {}, options?: RequestInit): Promise<TrendingLyricsItem[]> {
    const query = serializeFilterParams(params);
    return fetchApi<TrendingLyricsItem[]>(`/anonymous/lyrics/trending${query}`, options);
  },
  async search(params: LyricsSearchParams = {}, options?: RequestInit): Promise<LyricsSearchResult[]> {
    const qs = new URLSearchParams();
    qs.set('q', params.q ?? '');
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    return fetchApi<LyricsSearchResult[]>(`/anonymous/lyrics/search/query?${qs.toString()}`, options);
  },
  async autocomplete(params: LyricsSearchParams = {}, options?: RequestInit): Promise<LyricsSearchResult[]> {
    const qs = new URLSearchParams();
    qs.set('q', params.q ?? '');
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    return fetchApi<LyricsSearchResult[]>(`/anonymous/lyrics/search/autocomplete?${qs.toString()}`, options);
  },
};

export const adminLyricsService = {
  /**
   * Get Umami page stats for a single lyrics URL.
   * @param path - Full page path, e.g. `/versuri/cantec/vioara`
   * @param startAt - Unix timestamp in ms (start of period)
   * @param endAt   - Unix timestamp in ms (end of period)
   */
  async getPageStats(
    params: GetLyricsPageStatsParams
  ): Promise<LyricsPageStats> {
    const query = serializeFilterParams(params);
    return fetchApi<LyricsPageStats>(`/admin/analytics/lyrics/stats${query}`);
  },

  /**
   * Get top search queries for a specific lyrics slug (delegates to analytics admin endpoint)
   */
  async getTopSearchQueries(
    params: GetLyricsTopSearchQueriesParams,
  ): Promise<AdminTopSearchQuery[]> {
    const query = serializeFilterParams(params);
    return fetchApi<AdminTopSearchQuery[]>(`/admin/analytics/lyrics/cautari${query}`);
  },
};

// Admin lyrics linter service
export const adminLyricsLinterService = {
  async lintSlug(slug: string) {
    return fetchApi<BackgroundServiceTriggerResult>(`/admin/lyrics/lint/${encodeURIComponent(slug)}`, {
      method: "POST",
    });
  },
};

// Admin lyrics reports service (admin endpoints)
export const adminLyricsReportsService = {
  async getReports(params: AdminGetLyricsReportsParams = {}) {
    const query = serializeFilterParams(params);
    return fetchApi<Pagination<LyricsReport>>(`/admin/lyrics/reports${query}`);
  },

  async updateStatus(id: number, status: LyricsReportStatus) {
    return fetchApi<{ success: boolean; report: LyricsReport }>(`/admin/lyrics/reports/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  },

  async deleteReport(id: number) {
    return fetchApi<{ success: boolean }>(`/admin/lyrics/reports/${id}`, {
      method: "DELETE",
    });
  },

  async deleteResolved() {
    return fetchApi<{ success: boolean; deleted: number }>(`/admin/lyrics/reports/resolved`, {
      method: "DELETE",
    });
  },
};

export const anonymousLyricsReportsService = {
  async create(payload: AnonymousLyricsReportRequest) {
    return fetchApi<{ message: string; report?: unknown }>(
      "/anonymous/lyrics/reports",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  },
};

// Customer lyrics service (requires authentication)
export const customerLyricsService = {
  async getArtists(): Promise<Artist[]> {
    return fetchApi<Artist[]>("/customer/lyrics/artists");
  },

  async submitLyrics(payload: SubmitNewLyricsRequest): Promise<{ message: string; report?: LyricsReport }> {
    return fetchApi<{ message: string; report?: LyricsReport }>("/customer/lyrics/submit", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

// Admin lyrics files browser service
export const adminLyricsFilesService = {
  async listFiles(params: AdminGetLyricsFilesParams = {}): Promise<AdminLyricsFileEntry[]> {
    const query = serializeFilterParams(params);
    return fetchApi<AdminLyricsFileEntry[]>(`/admin/lyrics/files${query}`);
  },

  async getFile(filename: string): Promise<{ filename: string; content: string }> {
    return fetchApi<{ filename: string; content: string }>(`/admin/lyrics/files/${encodeURIComponent(filename)}`);
  },
};

// Admin lyrics YouTube search service
export const adminLyricsYoutubeService = {
  async searchVideos(q: string, maxResults?: number): Promise<AdminYouTubeSearchResult[]> {
    const params = new URLSearchParams({ q });
    if (maxResults !== undefined) params.set('maxResults', String(maxResults));
    return fetchApi<AdminYouTubeSearchResult[]>(`/admin/lyrics/youtube/search?${params}`);
  },

  async getTrending(params: AdminGetYoutubeTrendingParams = {}): Promise<ChildrenSongYoutubeTrending[]> {
    const qs = new URLSearchParams();
    if (params.forceRefresh !== undefined) qs.set('forceRefresh', String(params.forceRefresh));
    if (params.cachedOnly !== undefined) qs.set('cachedOnly', String(params.cachedOnly));
    const query = qs.toString();
    return fetchApi<ChildrenSongYoutubeTrending[]>(`/admin/lyrics/youtube/trending${query ? `?${query}` : ''}`);
  },
};
